import * as d3 from "d3";

import { timeframeToRange } from "src/organization/pages/project/environment/metrics/components/Chart/settings";
import {
  CurserDataIndexFuncProp,
  CurserDataType,
  LayoutDimension,
  PositionType
} from "src/organization/pages/project/environment/metrics/http2/types";
import { TimeFrameType } from "src/organization/pages/project/environment/metrics/resources/types";

export const move = (x: number, y: number) => {
  return `translate(${x}, ${y})`;
};

export const getLayoutPositions = (dms: LayoutDimension): PositionType => ({
  xRangeStart: dms.left,
  xRangeEnd: (dms?.width || 0) - dms.right,
  yRangeEnd: dms.bottom,
  yRangeStart: dms.height - dms.bottom,
  xTransform: move(0, dms.height - dms.bottom),
  yTransform: move(dms.left, dms.top),
  width: dms.width || 400,
  height: dms.height
});

type LinearAxisGeneratorProps = {
  xDomain: Array<any>;
  yDomain: Array<any>;
  dimension: LayoutDimension;
};

export const getLinearAxis = (props: LinearAxisGeneratorProps) => {
  const { xDomain, yDomain, dimension } = props;

  const positions = getLayoutPositions(dimension);

  const xScale = d3
    .scaleTime()
    .domain(xDomain)
    .range([positions.xRangeStart, positions.xRangeEnd]);

  const yScale = d3
    .scaleLinear()
    .domain(yDomain)
    .range([positions.yRangeStart, positions.yRangeEnd]);

  return {
    yScale,
    xScale,
    positions
  };
};

export const generateLabelProgression = (
  progressionCount: number,
  maxPoint: number = 0
) => {
  const totalDistance = maxPoint;
  const commonDifference = totalDistance / (progressionCount - 1);
  const generatedProgression: number[] = [];
  let currentValue = 0;

  for (let i = 0; i < progressionCount; i++) {
    generatedProgression.push(currentValue);
    currentValue += commonDifference;
  }

  return generatedProgression;
};

export const getCursorDataFunc = <D>(props: CurserDataIndexFuncProp<D>) => {
  const { data, bisectFn, yScale, xScale, setCursorData, disable, chartId } =
    props;

  if (!data) {
    return;
  }

  return disable
    ? undefined
    : event => {
        let activityId = null;
        if (event.target.classList.contains("activity-line")) {
          //Dont show tooltip when hovered on activity overlay
          activityId = event.target.getAttribute("data-activityId");
          // return callback(undefined);
        }
        if (!event.target.classList.contains("chart-graph")) {
          //Dont show tooltip when hovered on activity overlay
          // return callback(undefined);
        }

        const [relativeX, relativeY] = d3.pointer(event);
        const [absoluteX, absoluteY] = d3.pointer(event, document.body);
        const bisect = d3.bisector(bisectFn).center;
        const yInvert = yScale.invert(relativeY);
        const xInvert = xScale.invert(relativeX);
        const dataIndex = bisect(data, xInvert);
        const indexedData = data[dataIndex];
        setCursorData?.({
          activityId,
          absoluteX,
          absoluteY,
          yInvert,
          xInvert,
          dataIndex,
          indexedData,
          relativeX,
          relativeY,
          event,
          chartId
        });
      };
};

/**
 * Adapts cursor data from one chart to another, considering differences in scales and dimensions.
 **/
export const adaptCursorData = <D>(props: {
  cursorFuncProps: CurserDataIndexFuncProp<D>;
  cursorData?: CurserDataType;
}) => {
  const { data, bisectFn, yScale, xScale, disable } = props.cursorFuncProps;
  if (disable || !props.cursorData) return undefined;
  const { xInvert, relativeY, event } = props.cursorData;

  if (props.cursorData.chartId === props.cursorFuncProps.chartId) {
    //no need to recalculate it is the same chart
    return props.cursorData;
  }

  const bisect = d3.bisector(bisectFn).center;
  const yInvert = yScale.invert(relativeY);
  const relativeX = xScale(new Date(xInvert));
  const dataIndex = bisect(data, xInvert);
  const indexedData = data[dataIndex];
  return {
    ...props.cursorData,
    yInvert,
    dataIndex,
    indexedData,
    relativeX,
    event
  };
};

export const formatPercent = (value: number, total: number) =>
  value ? `${((value / total) * 100).toFixed(1)}%` : "0.0%";

/**
 * Formats a number with optional abbreviation.
 *
 * @param value - The number to format.
 * @param precision - The number of decimal places to include.
 * @param {boolean} abbreviateK - Whether to abbreviate thousands with 'k'.
 * @returns The formatted number as a string.
 */
export const formatNumber = ({
  value = 0,
  precision = 0,
  abbreviateK = true
}: {
  value?: number;
  precision?: number;
  abbreviateK?: boolean;
}) => {
  if (abbreviateK && value >= 1000) {
    return `${(value / 1000).toFixed(precision)}k`;
  }
  return precision !== undefined ? value.toFixed(precision) : value.toString();
};

export const getDefaultDomain = (
  timeframe?: TimeFrameType,
  defaultYMax = 5
) => {
  const range = timeframeToRange(timeframe);
  return { yDomain: [0, defaultYMax], xDomain: [range.start, range.end] };
};

/**
 * Returns the next number that is a multiple of the given divisor.
 * If the number is already a multiple, it returns the same number.
 *
 * @param num - The input number.
 * @param divisor - The divisor to find the next multiple of.
 * @returns The next multiple of the divisor that is greater than or equal to num.
 */
export const getNextDivisible = (num: number, divisor: number) => {
  return Math.ceil(num / divisor) * divisor;
};
